/**
 * 관리자 에셋 슬롯·규격 검증 — docs/build/09 §6/§9, 규격은 docs/build/06.
 * 관리자 UI·CI 양측에서 공유(순수·테스트 가능). 콘텐츠 주도: 슬롯은 content/asset-slots.json.
 */

export type AssetFormat =
  | "webp" | "png" | "jpg" | "json" | "mind"
  | "mp4" | "webm"
  | "mp3" | "m4a" | "ogg" | "wav";

export interface AssetSpec {
  formats: AssetFormat[]; // 허용 포맷(앞이 권장)
  maxKB?: number; // 파일 용량 상한
  maxLongEdgePx?: number; // 긴 변 픽셀 상한(페이지 2048 등)
  tileGrid?: number; // 타일 그리드(48) — 가로·세로가 배수여야 함
  alphaRequired?: boolean; // 투명 배경 권장(아바타·오브젝트)
}

export interface AssetSlot {
  key: string; // "book.page", "ar.poster"
  group: string; // "전자책"
  label: string;
  repeat?: "perPage" | "perZone" | "preset" | "single";
  /**
   * `repeat: "single"` 슬롯이 매니페스트에서 **실제로 쓰는 키**. 안 적으면 `<key>.default`.
   *
   * ## 왜 필요한가
   * 관리자는 단일 슬롯 키를 `<key>.default` 로 **지어냈다**. 그런데 웹이 읽는 키가
   * 그 규칙을 안 따르는 게 있었다 — 굿즈 마켓 배너는 `goods.market.banner`(뒤에 아무것도 안 붙음).
   * 그래서 배너가 화면에 멀쩡히 나오는데 관리자에서는 "비어 있음"이었고, 운영자가 올려도
   * 아무도 안 읽는 `goods.market.banner.default` 에 들어갔다.
   *
   * 키는 지어내지 말고 **여기 적힌 것 하나**를 쓴다. `assetSlots.test.ts` 가 웹 코드에
   * 그 키가 실제로 있는지 확인한다.
   */
  manifestKey?: string;
  spec: AssetSpec;
}

/** 단일 슬롯이 매니페스트에서 쓰는 키. 관리자·검사 스크립트가 **같이** 이 함수를 쓴다. */
export function singleSlotKey(slot: AssetSlot): string {
  return slot.manifestKey ?? `${slot.key}.default`;
}

/** 업로드 파일 메타(브라우저에서 추출). width/height 는 이미지일 때만. */
export interface AssetFileMeta {
  format: string; // 확장자/MIME 에서 추출(소문자)
  bytes: number;
  width?: number;
  height?: number;
  /**
   * 배경이 안 지워졌는가 — `inspectBackground()`(cutout.ts)로 **픽셀을 실제로 봐서** 채운다.
   * 못 본 경우 `undefined` 로 두면 막지 않고 경고만 한다(서버측 검사 등).
   *
   * 형식만 봐서는 알 수 없다. PNG 도 WebP 도 투명을 담을 수 있지만
   * **AI 가 만든 그림은 흰 배경이 칠해진 채로 나온다.**
   */
  hasBackground?: boolean;
}

/**
 * 무엇이 걸렸는지 **코드로** 알려 준다.
 *
 * 문구로 판별하면(예: 메시지에 "해상도" 가 들어 있나) 문구를 다듬는 순간 조용히 갈린다.
 * 관리자는 이 코드로 **「버튼 한 번이면 고칠 수 있는 것」과 「정말 안 되는 것」** 을 나눈다.
 */
export type ValidationCode =
  | "format" // 허용하지 않는 파일 형식
  | "size_kb" // 용량 초과
  | "long_edge" // 긴 변 초과 — 관리자가 줄여 줄 수 있다
  | "tile_grid" // 48px 배수 아님
  | "alpha_jpg" // JPG 라 투명을 담을 수 없다(형식을 바꾸는 수밖에 없다)
  | "background" // 배경이 안 지워졌다 — 관리자가 지워 줄 수 있다
  | "alpha_unknown"; // 픽셀을 못 봐서 확인만 요청

export interface ValidationIssue {
  level: "error" | "warn";
  message: string;
  code?: ValidationCode;
}

function normFormat(f: string): string {
  let x = f.toLowerCase().replace(/^(image|video|audio)\//, "").replace(/^\./, "");
  if (x === "jpeg") x = "jpg";
  if (x === "mpeg") x = "mp3";
  if (x === "x-m4a") x = "m4a";
  return x;
}

/** 슬롯 규격 대조. error 가 하나라도 있으면 게시 차단(FR-ADMIN-04). 절대 throw 안 함. */
export function validateAsset(
  spec: AssetSpec,
  meta: AssetFileMeta,
): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  if (!spec || !meta) return [{ level: "error", message: "잘못된 입력" }];

  const fmt = normFormat(meta.format);
  if (!spec.formats.map(String).includes(fmt)) {
    issues.push({
      level: "error",
      code: "format",
      message: `허용 포맷이 아니에요(${fmt}). 허용: ${spec.formats.join(", ")}`,
    });
  }

  if (spec.maxKB && Number.isFinite(meta.bytes)) {
    const kb = meta.bytes / 1024;
    if (kb > spec.maxKB) {
      issues.push({
        level: "error",
        code: "size_kb",
        message: `용량 초과 ${Math.round(kb)}KB > ${spec.maxKB}KB. 압축이 필요해요`,
      });
    }
  }

  if (spec.maxLongEdgePx && meta.width && meta.height) {
    const longEdge = Math.max(meta.width, meta.height);
    if (longEdge > spec.maxLongEdgePx) {
      issues.push({
        level: "error",
        code: "long_edge",
        message: `해상도 초과 ${longEdge}px > ${spec.maxLongEdgePx}px(긴 변)`,
      });
    }
  }

  if (spec.tileGrid && meta.width && meta.height) {
    const g = spec.tileGrid;
    if (meta.width % g !== 0 || meta.height % g !== 0) {
      issues.push({
        level: "error",
        code: "tile_grid",
        message: `타일 규격 위반: ${meta.width}×${meta.height}는 ${g}px 배수가 아니에요`,
      });
    }
  }

  if (spec.alphaRequired) {
    if (fmt === "jpg") {
      // jpg 는 투명을 담을 수 없다 — 형식만 보고 바로 막는다
      issues.push({ level: "error", code: "alpha_jpg", message: "투명 배경이 필요해요(JPG 불가, PNG/WebP 사용)" });
    } else if (meta.hasBackground === true) {
      // 🔴 픽셀을 실제로 본 결과. 예전에는 **PNG 를 검사조차 안 했다** — AI(GPT·제미나이)가
      // 만드는 가장 흔한 형식인데, 흰 배경 그대로 올라가면 마을에 **흰 네모가 뜬다.**
      issues.push({
        level: "error",
        code: "background",
        message: "배경이 안 지워졌어요 — 지우고 올리거나 「배경 지우기」를 눌러 주세요",
      });
    } else if (meta.hasBackground === undefined && (fmt === "png" || fmt === "webp")) {
      // 픽셀을 못 본 경우(서버 검사 등)는 막지 않고 확인만 요청한다
      issues.push({ level: "warn", code: "alpha_unknown", message: "투명 배경인지 확인해 주세요" });
    }
  }

  return issues;
}

export function isPublishable(issues: ValidationIssue[]): boolean {
  return !issues.some((i) => i.level === "error");
}

/** content/asset-slots.json → 안전한 슬롯 배열. */
export function normalizeSlots(input: unknown): AssetSlot[] {
  const arr = Array.isArray(input) ? input : [];
  return arr
    .map((s): AssetSlot | null => {
      if (!s || typeof s !== "object") return null;
      const o = s as Record<string, unknown>;
      const key = typeof o.key === "string" ? o.key : "";
      const spec = (o.spec ?? {}) as Record<string, unknown>;
      if (!key) return null;
      return {
        key,
        group: typeof o.group === "string" ? o.group : "기타",
        label: typeof o.label === "string" ? o.label : key,
        repeat:
          o.repeat === "perPage" || o.repeat === "perZone" || o.repeat === "preset"
            ? o.repeat
            : "single",
        // 키를 지어내지 않게 — 적혀 있으면 그대로, 없으면 `<key>.default`(singleSlotKey)
        ...(typeof o.manifestKey === "string" && o.manifestKey ? { manifestKey: o.manifestKey } : {}),
        spec: {
          formats: Array.isArray(spec.formats)
            ? (spec.formats.filter((f) => typeof f === "string") as AssetFormat[])
            : ["webp", "png"],
          maxKB: num(spec.maxKB),
          maxLongEdgePx: num(spec.maxLongEdgePx),
          tileGrid: num(spec.tileGrid),
          alphaRequired: spec.alphaRequired === true,
        },
      };
    })
    .filter((s): s is AssetSlot => s !== null);
}

function num(v: unknown): number | undefined {
  return typeof v === "number" && Number.isFinite(v) ? v : undefined;
}
